import {getMatchingPatterns} from "./utils";
import {attributeAnnotations, fixValidEnd, html, sortBy, truncate} from "../editor/utils";
import {direction, Selector} from "../mixins/dictionaries";

export default class Sidebar {
    constructor(settings, mediator) {
        this.settings = {
            ...settings
        }
        this.mediator = mediator;
        this.EventsOn();

        this.initContainer();
    }

    initData(data) {
        this.reads = data.result.reads;
        this.referenceLength = data.result.reference.length;
        this.annotations = data.result.annotations;

        this.tempStartOffsetAlignment = 0;
        this.tempEndOffsetAlignment = 0;

        this.reads.forEach(r => {
            if (r.successful) {
                if ((r.end + r.end_trim) > this.referenceLength) {
                    this.tempStartOffsetAlignment = Math.max(this.referenceLength - (r.start - r.start_trim), this.tempStartOffsetAlignment);
                    this.tempEndOffsetAlignment = Math.max((r.end + r.end_trim) - this.referenceLength, this.tempEndOffsetAlignment);
                }
            }
        })

        this.sequenceLength = this.referenceLength + this.tempStartOffsetAlignment + this.tempEndOffsetAlignment;
        this.positions = data.result.positions;

        if (this.sequenceLength > this.referenceLength) {
            this.positions = [
                ...this.positions.slice(this.referenceLength - this.tempStartOffsetAlignment, this.referenceLength),
                ...this.positions,
                ...this.positions.slice(0, this.tempEndOffsetAlignment)
            ];
        }

        this.referenceMatchingPatterns = getMatchingPatterns(0, 0, this.referenceLength, 0, data.result.mismatches, data.result.deletions, data.result.insertions);

        this.mismatcheMatchingPatterns = this.referenceMatchingPatterns.filter(mp => {
            return mp.type === "mis"
        })
        this.insertionMatchingPatterns = this.referenceMatchingPatterns.filter(mp => {
            return mp.type === "ins"
        })
        this.deletionMatchingPatterns = this.referenceMatchingPatterns.filter(mp => {
            return mp.type === "del"
        })
    }

    setData(data) {
        this.initData(data)
        this.render()
    }

    setMaxScroll(data) {
        this.maxBasePairsInAreaView = data.width;
    }

    render() {
        this.addAnnotationItems(this.annotations)

        this.addTableItems(this.$mismatchesContainer, this.mismatcheMatchingPatterns, this.$mismatchesCounter, "mismatches")
        this.addTableItems(this.$insertionsContainer, this.insertionMatchingPatterns, this.$insertionsCounter, "insertions")
        this.addTableItems(this.$deletionsContainer, this.deletionMatchingPatterns, this.$deletionsCounter, "deletions")
    }

    addTableItems(container, rows, counterContainer, label) {
        const addItem = (mp) => {
            const relativeStart = this.positions[this.tempStartOffsetAlignment + mp.start];
            const relativeEnd = this.positions[this.tempStartOffsetAlignment + mp.end];
            const item = document.createElement("li");
            item.classList.add("list-group-item", "list-group-item-action", "align-items-center", "item");

            const clickableItem = document.createElement("a");
            clickableItem.classList.add("d-flex", "justify-content-between")
            clickableItem.href = "#"

            const range = document.createElement("div");
            range.innerHTML = `from Bp: <b>${relativeStart + 1}</b> - to Bp: <b>${relativeEnd}</b>`
            const length = document.createElement("div");
            length.innerHTML = `length: <span class="badge badge-warning">${mp.length}</span> bp`

            clickableItem.appendChild(range);
            clickableItem.appendChild(length);
            item.appendChild(clickableItem)
            return item
        }

        let countRows = 0;
        rows.forEach(r => {
            const item = addItem(r)
            container.append(item)
            this.addSelectionEvent(item, r.start, r.length)
            countRows += r.length
        })

        if (rows.length === 0) {
            container.append(this.noRows(`There are no ${label} in this alignment`));
        }
    }

    addAnnotationItems(annotations) {
        const listItems = document.createElement('ul');
        listItems.classList.add('list-group');
        const orderedList = annotations.sort(sortBy("start"));

        orderedList.forEach(a => {
            const relativeStart = this.positions[this.tempStartOffsetAlignment + a.start];
            const relativeEnd = this.positions[this.tempStartOffsetAlignment + a.end] || this.referenceLength;

            const relativeLength = relativeEnd - relativeStart;
            const attributeAnnotation = attributeAnnotations(a.role_id, this.settings.annotation_colors);
            const name = a.name ? a.name : "Unknown " + attributeAnnotation.name

            const itemList = document.createElement('li');
            itemList.classList.add('list-group-item', 'list-group-item-action');
            itemList.dataset.start = a.start;
            itemList.dataset.end = a.end;

            const mainItemContainer = document.createElement('div');
            mainItemContainer.classList.add('d-flex', 'align-items-center', 'justify-content-between', 'p2', 'item');

            const leftContainer = document.createElement('div');
            leftContainer.classList.add('d-flex', 'd-inline-flex', 'me-2');

            const checkBoxContainer = document.createElement('div');
            checkBoxContainer.classList.add('d-flex', 'form-check', 'align-items-center');

            const checkBox = document.createElement('input');
            checkBox.classList.add('form-check-input', 'item' + a.pk);
            checkBox.id = a.pk;
            checkBox.dataset.pk = a.pk;
            checkBox.setAttribute('type', 'checkbox')
            checkBox.checked = true;

            const checkBoxLabel = document.createElement('label');
            checkBoxLabel.classList.add('form-check-label', 'visually-hidden');
            checkBoxLabel.setAttribute("for", a.pk);
            checkBoxLabel.innerText = "Show/hide";

            checkBoxContainer.appendChild(checkBox)
            checkBoxContainer.appendChild(checkBoxLabel)

            const dataAnnotationContainer = document.createElement('ul');
            dataAnnotationContainer.classList.add('list-unstyled', 'z-index-0', 'p-0');

            const annotationName = document.createElement('li');
            annotationName.innerText = "Name: " + truncate(name, 15)
            const annotationType = document.createElement('li');
            annotationType.innerText = "Type: " + truncate(attributeAnnotation.name, 15)
            const annotationDirection = document.createElement('li');
            annotationDirection.innerText = "Direction: " + direction[a.direction]
            const annotationLength = document.createElement('li');
            annotationLength.innerText = "Length: " + relativeLength;

            dataAnnotationContainer.appendChild(annotationName);
            dataAnnotationContainer.appendChild(annotationType);
            dataAnnotationContainer.appendChild(annotationDirection);
            dataAnnotationContainer.appendChild(annotationLength);

            leftContainer.appendChild(checkBoxContainer);
            leftContainer.appendChild(dataAnnotationContainer);

            const range = document.createElement("a");
            range.setAttribute('href', "#");
            range.classList.add('d-flex', 'align-items-center', 'link-primary');
            range.innerText = `${relativeStart + 1}..${relativeEnd}`

            this.addSelectionEvent(range, a.start, (a.end - a.start))

            mainItemContainer.appendChild(leftContainer);
            mainItemContainer.appendChild(range);

            itemList.appendChild(mainItemContainer)


            listItems.appendChild(itemList)
        })
        this.$annotationsContainer.appendChild(listItems);
        this.toggleItemCheckboxes(listItems, "annotations");
    }

    addSelectionEvent(item, position, length) {
        const minEdgeOffsetScroll = 0;
        const maxEdgeOffsetScroll = Math.round(this.sequenceLength - (this.maxBasePairsInAreaView / 2));

        item.addEventListener("click", e => {
            e.preventDefault();
            e.stopPropagation();

            let currentOffsetScroll = Math.round(this.tempStartOffsetAlignment + position - (this.maxBasePairsInAreaView / 2));

            if (currentOffsetScroll < minEdgeOffsetScroll || (currentOffsetScroll + (this.maxBasePairsInAreaView / 2)) > maxEdgeOffsetScroll) {
                if (currentOffsetScroll < minEdgeOffsetScroll) {
                    currentOffsetScroll = minEdgeOffsetScroll;
                } else {
                    currentOffsetScroll = maxEdgeOffsetScroll;
                }
            }

            this.EventsTrigger("setOffsetScroll", {current: currentOffsetScroll})
            this.EventsTrigger("slide", "end")

            const endWrappingStart = length < 0 ? this.referenceLength + item.end : position + length;

            this.EventsTrigger("selectRange", {
                start: this.tempStartOffsetAlignment + position,
                end: this.tempStartOffsetAlignment + endWrappingStart
            })
        })
    }

    toggleItemCheckboxes(content, selector) {
        const checkBoxItems = content.querySelectorAll("input[type=checkbox]")

        checkBoxItems.forEach(el => {
            el.addEventListener("click", e => {
                this.EventsTrigger("toggleObjects", {
                    "selector": "annotations",
                    "objects": [parseInt(el.id)],
                    "visible": el.checked
                })
            })
        })
    }

    initContainer() {
        this.$annotationsContainer = document.querySelector("#sidebarTabContent #annotations ul");

        this.$mismatchesContainer = document.querySelector("#sidebarTabContent #mismatches ul");
        this.$insertionsContainer = document.querySelector("#sidebarTabContent #insertions ul");
        this.$deletionsContainer = document.querySelector("#sidebarTabContent #deletions ul");
    }

    noRows(text) {
        const noRowsItem = document.createElement("li");
        noRowsItem.classList.add('h-100', 'mt-3', 'p-5', 'text-bg-secondary', 'rounded-3', 'text-center', 'list-group-item')
        noRowsItem.innerText = text;

        return noRowsItem;
    }

    EventsTrigger(action, data) {
        if (this.mediator) {
            this.mediator.trigger(action, data, this)
        }
    }

    EventsOn() {
        if (this.mediator) {
            this.mediator.on("setData", "setData", this);
            this.mediator.on("setAreaView", "setMaxScroll", this);
        }
    }
}